import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ----- URL classifier -----
function classifyUrl(url) {
  if (!url) return { kind: "link" };
  const u = url.trim();
  // YouTube
  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) {
    return {
      kind: "iframe",
      src: `https://www.youtube.com/embed/${yt[1]}?rel=0&modestbranding=1`,
    };
  }
  // Vimeo
  const vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) {
    return { kind: "iframe", src: `https://player.vimeo.com/video/${vm[1]}` };
  }
  // Loom
  const lm = u.match(/loom\.com\/share\/([a-z0-9]+)/i);
  if (lm) {
    return { kind: "iframe", src: `https://www.loom.com/embed/${lm[1]}` };
  }
  // Direct video file
  if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(u)) {
    return { kind: "video", src: u };
  }
  // Direct audio file
  if (/\.(mp3|wav|m4a|aac|ogg)(\?|$)/i.test(u)) {
    return { kind: "audio", src: u };
  }
  // Generic link (Google Drive, Dropbox, S3 signed, etc.)
  return { kind: "link", src: u };
}

export default function Recording() {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    document.title = "S.P.A.C.E. Academy — Your Recorded Reading";
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/recording/validate`, { params: { token } });
        if (!cancelled) setState({ status: "ok", data: res.data });
      } catch (err) {
        if (!cancelled) {
          setState({
            status: "error",
            message: err?.response?.data?.detail || "This recording link is invalid.",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (state.status === "loading") {
    return (
      <div className="recording-page" data-testid="recording-loading">
        <div className="recording-shell">
          <p className="recording-eyebrow">S.P.A.C.E. Academy</p>
          <h1 className="recording-title">Loading your recording…</h1>
        </div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="recording-page" data-testid="recording-error">
        <div className="recording-shell">
          <p className="recording-eyebrow">S.P.A.C.E. Academy</p>
          <h1 className="recording-title">This link cannot be opened.</h1>
          <p className="recording-sub">{state.message}</p>
          <p className="recording-help">
            If you believe this is in error, reply to the email Dr. Will Rogers sent you and
            we'll re-issue a fresh access link.
          </p>
        </div>
      </div>
    );
  }

  const { name, recording_url, note, view_count } = state.data;
  const classified = classifyUrl(recording_url);

  return (
    <div className="recording-page" data-testid="recording-root">
      <div className="recording-shell">
        <p className="recording-eyebrow">S.P.A.C.E. Academy · Tier 03 · Recorded Reading</p>
        <h1 className="recording-title" data-testid="recording-greeting">
          Welcome, {name}.
        </h1>
        <p className="recording-sub">
          Your private 30-minute reading from Dr. Will Rogers is below. You may return to
          this link as many times as you need.
        </p>

        <div className="recording-frame" data-testid="recording-frame">
          {classified.kind === "iframe" && (
            <div className="recording-aspect">
              <iframe
                src={classified.src}
                title="S.P.A.C.E. Recorded Reading"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                data-testid="recording-iframe"
              />
            </div>
          )}
          {classified.kind === "video" && (
            <video
              src={classified.src}
              controls
              playsInline
              className="recording-video"
              data-testid="recording-video"
            />
          )}
          {classified.kind === "audio" && (
            <audio
              src={classified.src}
              controls
              className="recording-audio"
              data-testid="recording-audio"
            />
          )}
          {classified.kind === "link" && (
            <div className="recording-fallback">
              <p>This recording opens in a new tab.</p>
              <a
                href={classified.src}
                target="_blank"
                rel="noopener noreferrer"
                className="recording-open-btn"
                data-testid="recording-open-link"
              >
                Open My Recording
              </a>
            </div>
          )}
        </div>

        {note && (
          <div className="recording-note" data-testid="recording-note">
            <p className="recording-note-label">A note from Dr. Will Rogers</p>
            <p className="recording-note-body">{note}</p>
          </div>
        )}

        <p className="recording-help" data-testid="recording-views">
          Views on this link: {view_count}. Keep this page bookmarked — it remains active
          for your personal use.
        </p>

        <hr className="recording-rule" />
        <p className="recording-signoff">
          In service and in fire,<br />
          <span className="recording-signoff-name">Dr. Will Rogers · MasterTeacher33</span>
        </p>
      </div>
    </div>
  );
}
